import {
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  theme,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { useMyProfileModel } from '@common/models/MyProfileModel';
import { fetchAuthSession } from 'aws-amplify/auth';
import { JwtPayload } from '@aws-amplify/core/internals/utils';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSession } from 'src/apps/invest/common/hooks/useSession';
import { UI } from 'src/apps/invest/common/shells/UI';
import { ROUTES } from 'src/apps/invest/config';
import { useAccountInfoViewModel } from '../models/InfoViewModel';
import { InfoForm } from '../types';
import { AddressForm } from './AddressForm';
import { AlertDialog } from './AlertDialog';
import { AvatarUploader } from './AvatarUploader';
import { EmailField } from './EmailField';
import { PhoneNumberField } from './PhoneNumberField';

export const AccountInformationPage: React.FC = () => {
  const viewModel = useAccountInfoViewModel();
  const { investorAccount, fetchInvestorAccount } = useInvestorAccountModel();
  const [user, setUser] = useState<JwtPayload | null>(null);
  const session = useSession().session;
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const infoForm = useForm<InfoForm>({ mode: 'onTouched' });
  const myProfileModel = useMyProfileModel();

  const loadAccountInfo = async () => {
    viewModel.setState('LOADING');
    await fetchInvestorAccount(); // fetch investor account
    if (!session?.investorId) return;
    const amplifySession = await fetchAuthSession();
    setUser(amplifySession.tokens?.idToken?.payload || null);
    viewModel.setState('RENDER');
  };

  useEffect(() => {
    loadAccountInfo();
  }, []);

  let content;
  switch (viewModel.state) {
    case 'LOADING':
      content = (
        <VStack>
          <Spinner color="blue.500" />
        </VStack>
      );
      break;
    case 'RENDER':
      content = (
        <Flex w="full" flexDirection={'column'} gap={5} justifyContent="flex-start">
          <AvatarUploader isOpen={isOpen} onClose={onClose} />

          <Heading fontSize={'24px'}>Account Information</Heading>
          <HStack
            w="full"
            border={`1px solid ${theme.colors.gray[200]}`}
            borderRadius={'10px'}
            padding="20px"
            gap="10px">
            <Tooltip label="Edit Image" hasArrow={true} placement="top">
              <Avatar
                border={`3px solid ${theme.colors.white}`}
                cursor={'pointer'}
                name={session?.name}
                src={myProfileModel.myProfile?.headshot?.download_url ?? ''}
                onClick={onOpen}
                padding={'3px'}
                boxSize="56px"
                _hover={{
                  border: `3px solid ${theme.colors.blue[500]}`,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
            <Box>
              <TextBlock color={'gray.600'}>Name</TextBlock>
              <TextBlock weight="bold">{`${investorAccount?.individual_details?.first_name || ''} ${
                investorAccount?.individual_details?.last_name || ''
              }`}</TextBlock>
            </Box>
          </HStack>
          <Box w="full" border={`1px solid ${theme.colors.gray[200]}`} borderRadius={'10px'} padding="20px">
            <EmailField
              infoForm={infoForm}
              currentEmail={user?.email as string | null}
              showEmailDialog={() => {
                setShowEmailDialog(true);
              }}
            />
          </Box>
          <Box w="full" border={`1px solid ${theme.colors.gray[200]}`} borderRadius={'10px'} padding="20px">
            <PhoneNumberField infoForm={infoForm} currentPhoneNumber={user?.phone_number as string | null} />
          </Box>
          <Box w="full" border={`1px solid ${theme.colors.gray[200]}`} borderRadius={'10px'} padding="20px">
            <AddressForm infoForm={infoForm} />
          </Box>
          <Box w="full" textAlign={'center'} padding="20px">
            <Button as={Link} to={ROUTES.resetPassword}>
              Reset Password
            </Button>
          </Box>
          <AlertDialog
            onClose={() => setShowEmailDialog(false)}
            onOk={() => setShowEmailDialog(false)}
            onCancel={() => setShowEmailDialog(false)}
            isOpen={showEmailDialog}
            title={'Verification Email Sent'}
            message={`A verification email has been sent to ${
              infoForm.getValues().email
            }. Please confirm your email address, then refresh this page.`}
          />
        </Flex>
      );
  }

  return <UI>{content}</UI>;
};
